import { faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import {
  CompanyInfoCard,
  CompanyLocationCard,
} from '@/components/shared/CompanyProfileCards';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs from '@/components/shared/PageTabs';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { UserInformationCard } from '../../-components/UserInformationCard';
import { UserProfileHeader } from '../../-components/UserProfileHeader';
import { userDetailQueryOptions } from '../../-hooks/useUserDetail';
import { useUserDetailPage } from '../../-hooks/useUserDetailPage';

export const Route = createFileRoute('/(client)/users/(pages)/$id/')({
  /**
   * Swallowed on failure: the loader is a prefetch, and letting it throw hands
   * the route to the router's error boundary, which replaces the page and
   * hides the inline error state below.
   */
  loader: ({ params }) =>
    queryClient
      .ensureQueryData(userDetailQueryOptions(params.id))
      .catch(() => undefined),
  component: UserProfilePage,
});

function UserProfilePage() {
  const { id } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'userProfile' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const page = useUserDetailPage(id, t);

  // The crumb is the user's id otherwise. The loader has already put them in
  // cache, so this costs no request.
  const name = page.user
    ? [page.user.firstName, page.user.lastName].filter(Boolean).join(' ') ||
      page.user.email
    : null;
  useBreadcrumbLabel(id, name);

  if (page.isLoading) {
    return (
      <PageContainer>
        <div className='flex flex-col gap-4 max-w-5xl mx-auto w-full'>
          <Skeleton className='h-28 w-full' />
          <Skeleton className='h-64 w-full' />
        </div>
      </PageContainer>
    );
  }

  if (page.isError || !page.user) {
    return (
      <PageContainer>
        <QueryErrorState error={page.error} title={t('detail.loadFailed')} />
      </PageContainer>
    );
  }

  const company = page.user.company;
  const hasLocation = Boolean(
    company && (company.addressLine1 ?? company.city ?? company.country),
  );

  // Header, then tabs, then the content — the same three-part layout
  // `/profile` uses, so a colleague's profile reads as the same kind of screen
  // as your own.
  //
  // One tab, as the old page had: it names what is on screen rather than
  // offering a choice, and leaves somewhere obvious for a second to go.
  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <UserProfileHeader user={page.user} isMyProfile={page.isMyProfile} />

      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs
          tabs={[
            {
              key: 'overview',
              label: t('detail.tabs.overview'),
              icon: <FontAwesomeIcon icon={faUser} />,
              // Resolved, not the `$id` pattern: `useActivePathTab`
              // compares it to `location.pathname` as a plain string, so the
              // pattern never matches and the tab never reads as active.
              to: `/users/${id}`,
            },
          ]}
          mode='path'
          moreLabel={tShared('moreTabs')}
        />
      </div>

      <PageContainer>
        <div className='flex flex-col gap-4 max-w-5xl mx-auto w-full'>
          <UserInformationCard
            user={page.user}
            canEdit={page.canEdit}
            isSaving={page.isSaving}
            onToggleBlocked={blocked => void page.toggleBlocked(blocked)}
          />

          {company && <CompanyInfoCard company={company} />}
          {company && hasLocation && <CompanyLocationCard company={company} />}
        </div>
      </PageContainer>
    </div>
  );
}
